import React from "react";

const colors = {
  brand: "#1E293B",
  maritime: "#03f8d7",
  success: "#03f8d7",
  textMuted: "#94a3b8",
  border: "rgba(255, 255, 255, 0.08)",
  bg: "#0a0a0b"
};

function Footer() {
  const suiteName = "Maritime Supply Suite";
  
  // Extract tenant scope information for dynamic branding/domain display
  const tenantId = localStorage.getItem("adminId") || "global";
  const activeDomain = tenantId !== "global" ? `${tenantId}.maritimesupplysuite.shop` : "www.maritimesupplysuite.shop";

  return (
    <footer className="footer-container" data-tenant-scope={tenantId}>
      <div className="status-row">
        <div className="status-indicator">
          <div className="heartbeat"></div>
          <span className="status-text">UPLINK ACTIVE</span>
        </div>
        
        <div className="compliance-group">
          <span className="iso-text">ISO 27001 SECURED</span>
          <div className="star-container">
            {[1, 2, 3, 4, 5].map((s) => (
              <span key={s} className="star-box">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
              </span>
            ))}
          </div>
          <span className="rating-text">NETWORK RATING</span>
        </div>
      </div>

      <div className="divider"></div>

      <div className="footer-bottom">
        <div className="copy-group">
          <p className="copyright-text">
            © 2026 {suiteName}.<br className="mobile-only" /> Industrial Intelligence Infrastructure.
          </p>
          <span className="version-tag">v4.2.0-STABLE</span>
        </div>

        <div className="link-group">
          <a href={`https://${activeDomain}`} className="domain-display" target="_blank" rel="noopener noreferrer">
            {activeDomain}
          </a>
          <div className="internal-links">
            <a href="mailto:support@maritimesupplysuite.shop" className="footer-link">Support</a>
            <span className="footer-link">Security</span>
            <span className="footer-link">Compliance</span>
            <span className="footer-link">SLA</span>
          </div>
        </div>
      </div>

      <style>{`
        .footer-container {
          background-color: ${colors.bg};
          padding: 40px 5%;
          border-top: 1px solid ${colors.border};
          margin-top: auto;
          font-family: 'Inter', sans-serif;
          z-index: 10;
          position: relative;
        }

        .status-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 30px;
          flex-wrap: wrap;
          gap: 20px;
        }

        .status-indicator {
          display: flex;
          align-items: center;
          gap: 12px;
          background: rgba(255,255,255,0.03);
          padding: 8px 16px;
          border-radius: 30px;
          border: 1px solid ${colors.border};
        }

        .heartbeat {
          width: 8px;
          height: 8px;
          background-color: ${colors.maritime};
          border-radius: 50%;
          animation: blink-teal 2s infinite ease-in-out;
          box-shadow: 0 0 8px ${colors.maritime};
        }

        .status-text {
          font-size: 10px;
          font-weight: 900;
          color: ${colors.maritime};
          letter-spacing: 1.5px;
        }

        .compliance-group {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .iso-text, .rating-text {
          font-size: 10px;
          font-weight: 800;
          color: ${colors.textMuted};
          letter-spacing: 1px;
        }

        .star-container {
          display: flex;
          gap: 3px;
          color: ${colors.maritime};
        }

        .divider {
          height: 1px;
          background: ${colors.border};
          margin-bottom: 30px;
        }

        .footer-bottom {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          flex-wrap: wrap;
          gap: 25px;
        }

        .copy-group {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .copyright-text {
          font-size: 12px;
          color: ${colors.textMuted};
          margin: 0;
          line-height: 1.5;
        }

        .version-tag {
          font-size: 9px;
          width: fit-content;
          background: rgba(3, 248, 215, 0.05);
          color: ${colors.maritime};
          padding: 2px 8px;
          border-radius: 4px;
          border: 1px solid rgba(3, 248, 215, 0.2);
          font-family: monospace;
        }

        .link-group {
          display: flex;
          flex-direction: column;
          gap: 15px;
          align-items: flex-end;
        }

        .domain-display {
          font-size: 12px;
          font-weight: 800;
          color: ${colors.maritime};
          text-decoration: none;
          letter-spacing: 0.5px;
        }

        .internal-links {
          display: flex;
          gap: 15px;
          flex-wrap: wrap;
          justify-content: flex-end;
        }

        .footer-link {
          font-size: 11px;
          font-weight: 700;
          color: ${colors.textMuted};
          text-transform: uppercase;
          text-decoration: none;
          letter-spacing: 1px;
          cursor: pointer;
          transition: 0.2s;
        }

        .footer-link:hover {
          color: ${colors.maritime};
        }

        .mobile-only { display: none; }

        @keyframes blink-teal {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(1.1); }
        }

        /* Responsive Adjustments */
        @media (max-width: 768px) {
          .status-row { justify-content: center; text-align: center; }
          .footer-bottom { flex-direction: column; align-items: center; text-align: center; }
          .link-group { align-items: center; }
          .internal-links { justify-content: center; gap: 10px; }
          .copy-group { align-items: center; }
          .mobile-only { display: block; }
          .footer-container { padding-bottom: 60px; }
        }
      `}</style>
    </footer>
  );
}

export default Footer;